import React, { Component, ReactElement } from 'react'
import ReactTable, { TableProps } from 'react-table'
import { debounce } from 'lodash'
import { t, Trans } from '@lingui/macro'
import classNames from 'classnames'
import 'react-table/react-table.css'

import { TableExtendedHeader } from './table-extended-header/TableExtendedHeader'
import TablePagination from './table-extended-pagination/TablePagination'
import ResettableInput from '../form/input/ResettableInput'
import Loader from '../loader/Loader'
import { Box } from 'front-end/src/components'

import './TableExtendedOld.less'

const SEARCH_REQUEST_DEBOUNCE_TIME = 500

interface ITableExtended {
  showSearchFilter?: boolean
  defaultSearch?: string
  loading?: boolean
  extendFilter?: ReactElement
  tableHeader?: string | ReactElement
  tableDescription?: string | ReactElement
  filterFields?: string[]
  onSearchChange?: (value: string) => void
  placeholder?: string
  paginationContentBefore?: any
  paginationContentAfter?: any
  alwaysShowHeader?: boolean
  filterContentBefore?: ReactElement
}

interface ITableExtendedState {
  search: string
}

// make table params optional
type IReactTableOptional<T> = { [P in keyof T]?: T[P] }

type ITableExtendedProps = ITableExtended & IReactTableOptional<TableProps>

export const DEFAULT_PAGE_LIMIT = 50

export const DEFAULT_TR_PROPS = {
  role: 'link',
  tabIndex: 0,
  onKeyPress: (ev) => {
    // Accessibility - enter triggers click
    if (ev.key === 'Enter') {
      // @ts-expect-error
      document?.activeElement?.click()
    }
  }
}

/**
 * @deprecated
 * Use newer version of TableReact with <TableExtended/> from front-end/src/components/common/table-extended
 */
export default class TableExtended extends Component<
  ITableExtendedProps,
  ITableExtendedState
> {
  constructor(props) {
    super(props)
    this.state = {
      search: props.defaultSearch || ''
    }

    this.sendChangeRequest = debounce(
      this.sendChangeRequest,
      SEARCH_REQUEST_DEBOUNCE_TIME
    )
  }

  onSearch = (value) => {
    this.setState(
      {
        search: value
      },
      this.sendChangeRequest
    )
  }

  onSearchReset = () => {
    this.setState({
      search: ''
    })
    if (this.props.onSearchChange) {
      this.props.onSearchChange('')
    }
  }

  sendChangeRequest() {
    if (this.props.onSearchChange) {
      this.props.onSearchChange(this.state.search)
    }
  }

  render() {
    const { search } = this.state
    const {
      showSearchFilter,
      paginationContentBefore,
      paginationContentAfter,
      tableHeader,
      tableDescription,
      data,
      loading,
      extendFilter = null,
      placeholder = t`No data`,
      defaultPageSize = DEFAULT_PAGE_LIMIT,
      className,
      filterContentBefore
    } = this.props
    const noDataMessage = loading ? '' : placeholder

    return (
      <div
        className={classNames(
          'TableExtended',
          {
            isEmpty: !data?.length && !this.props.alwaysShowHeader
          },
          className
        )}
      >
        {(tableHeader ||
          tableDescription ||
          showSearchFilter ||
          extendFilter) && (
          <TableExtendedHeader
            title={tableHeader}
            description={tableDescription}
            filter={
              <Box className="TableExtended__filter" justifyContent="flex-end">
                {filterContentBefore}
                {showSearchFilter && (
                  <ResettableInput
                    onChange={this.onSearch}
                    placeholder={t`Search`}
                    title={t`Search`}
                    showResetButton={!!search}
                    showSearchIcon
                    value={search}
                    onReset={this.onSearchReset}
                  />
                )}
                {extendFilter}
              </Box>
            }
          />
        )}
        <ReactTable
          PaginationComponent={(props) => (
            <TablePagination
              {...props}
              paginationContentBefore={paginationContentBefore}
              paginationContentAfter={paginationContentAfter}
              previousText={t`Prev`}
            />
          )}
          {...this.props}
          defaultPageSize={defaultPageSize}
          NoDataComponent={() => (
            <div className="rt-noData">{noDataMessage}</div>
          )}
          data={data}
          loading={loading}
          loadingText={
            <>
              <Loader /> <Trans>Loading...</Trans>
            </>
          }
        />
      </div>
    )
  }
}
